import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { type AutocompleteChangeReason, AutocompleteInputChangeReason } from "@mui/material";
import { debounce, DebouncedFunc } from "lodash";
import { AutoObject } from "../components/autocomplete/InfiniteAutocomplete";

export interface InfiniteAutocompleteProps {
  data: AutoObject[] | null | undefined;
  label: string;
  loading: boolean;
  isMoreToFetch: boolean;
  fetchMore: () => void;
  onChange?: (id: string, value: string) => void;
  onClear?: () => void;
  searchBy: (value: string) => void;
  onSelectRemoveSelected?: boolean;
  noOptionNode?: ReactNode;
  customRenderOption?: (props: React.HTMLAttributes<HTMLLIElement>, object: AutoObject) => JSX.Element;
  error?: string;
  small?: boolean;
  controllState?: {
    state: AutoObject | undefined;
    setState: (value: AutoObject) => void;
  };
}

interface InfiniteAutocompleteService {
  options: AutoObject[];
  handleInputChange: (value: string, reason: AutocompleteInputChangeReason) => void;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  autoRef: React.MutableRefObject<HTMLElement | null>;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  state: AutoObject;
  onSelectChange: (value: string | null, reason: AutocompleteChangeReason) => void;
  inputChangeLoading: boolean;
}

export function useInfiniteAutocomplete({
  data,
  onChange,
  searchBy,
  onSelectRemoveSelected,
  onClear,
  controllState,
}: InfiniteAutocompleteProps): InfiniteAutocompleteService {
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<AutoObject>({
    id: "",
    value: "",
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const autoRef = useRef<HTMLElement | null>(null);

  const debounceRef = useRef<DebouncedFunc<(criteria: string) => void> | undefined>();

  const options = (() => {
    return data || [];
  })();

  const handleInputChange = useCallback(
    (value: string, reason: AutocompleteInputChangeReason, setValue: (value: AutoObject) => void) => {
      setOpen(false);

      debounceRef.current?.cancel();
      debounceRef.current = debounce(() => {
        if (reason === "reset" || reason === "clear") {
          if (inputRef?.current) {
            if (onSelectRemoveSelected) {
              setValue({
                id: "",
                value: "",
              });
            }
            searchBy("");
            inputRef.current.blur();
          }
          if (reason === "clear") {
            setOpen(false);
          }
          return;
        }
        searchBy(value);
        setOpen(true);
      }, 800);
      debounceRef.current(value);
    },
    [searchBy, onSelectRemoveSelected, inputRef]
  );

  const onSelectChange = useCallback(
    (value: string | null, reason: AutocompleteChangeReason, setValue: (value: AutoObject) => void) => {
      if (reason === "clear") {
        setValue({
          id: "",
          value: "",
        });
        if (onClear) {
          onClear();
        }

        return;
      }
      const selected = options.find((x) => x.value === value) ?? {
        id: "",
        value: "",
      };
      if (selected?.id === "") {
        return;
      }
      setValue(selected);
      if (selected && onChange) {
        onChange(selected.id, selected.value);
      }
      setOpen(false);
    },
    [onChange, onClear, options]
  );

  useEffect(() => {
    if (data) {
      setInputChangeLoading(false);
    }
  }, [data]);

  const [inputChangeLoading, setInputChangeLoading] = useState(false);

  return {
    options,
    handleInputChange: (value, reason) =>
      controllState
        ? handleInputChange(value, reason, controllState.setState)
        : handleInputChange(value, reason, setValue),
    open,
    setOpen,
    autoRef,
    inputRef,
    state: controllState ? (controllState.state ? controllState.state : { id: "", value: "" }) : value,
    onSelectChange: (value, reason) =>
      controllState ? onSelectChange(value, reason, controllState.setState) : onSelectChange(value, reason, setValue),
    inputChangeLoading,
  };
}
